/**
 * Sports Main API Service
 * Handles the new sportsmain API endpoints for fetching sports data
 */

import { API_URL } from '@/utils/constants';

/**
 * Fetch all matches for a sport
 * @param {number} sid - Sport ID
 * @param {number} timeout - Request timeout in ms
 * @returns {Promise<Object>} API response with match list
 */
export const fetchAllSportsData = async (sid, timeout = 8000) => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    const res = await fetch(`${API_URL}/api/sportsmain/get-all-sports-data`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sid }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    if (err.name === 'AbortError') {
      console.warn(`Sports data fetch timeout for sid ${sid}`);
    } else {
      console.error('Error fetching all sports data:', err);
    }
    return null;
  }
};

/**
 * Fetch detailed match data by sport ID and game ID
 * @param {number} sid - Sport ID
 * @param {number|string} gmid - Game/Match ID
 * @param {number} timeout - Request timeout in ms
 * @returns {Promise<Object>} API response with detailed match data
 */
export const fetchSportsDataById = async (sid, gmid, timeout = 8000) => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    const res = await fetch(`${API_URL}/api/sportsmain/get-sports-data-id`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sid, gmid }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!res.ok) return null;
    return await res.json();
  } catch (err) {
    if (err.name === 'AbortError') {
      console.warn(`Sports detail fetch timeout for gmid ${gmid}`);
    } else {
      console.error('Error fetching sports data by ID:', err);
    }
    return null;
  }
};

// ─── Live Stream & Scorecard APIs ────────────────────────

/**
 * Fetch live stream URL for a match
 * @param {number} sid - Sport ID
 * @param {number|string} gmid - Game/Match ID
 * @returns {Promise<Object|null>} { id, stream_url } or null
 */
export const fetchLiveStream = async (sid, gmid) => {
  try {
    const res = await fetch(`${API_URL}/api/sportsmain/get-live-stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sid, gmid }),
    });
    const data = await res.json();
    if (data?.success && data?.data?.stream_url) return data.data;
    return null;
  } catch {
    return null;
  }
};

/**
 * Fetch scorecard URL for a match
 * @param {number} sid - Sport ID
 * @param {number|string} gmid - Game/Match ID
 * @returns {Promise<Object|null>} { etid, gmid, scorecard_url } or null
 */
export const fetchScorecard = async (sid, gmid) => {
  try {
    const res = await fetch(`${API_URL}/api/sportsmain/get-scorecard`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sid, gmid }),
    });
    const data = await res.json();
    if (data?.success && data?.data?.scorecard_url) return data.data;
    return null;
  } catch {
    return null;
  }
};

// ─── Bet Placement & Open Bets APIs ─────────────────────

const getAuthToken = () => {
  try {
    const raw = localStorage.getItem('token');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return localStorage.getItem('token');
  }
};

/**
 * Place a bet
 * @param {Object} payload - Full bet payload
 * @returns {Promise<Object>} API response
 */
export const placeBet = async (payload) => {
  try {
    const token = getAuthToken();
    const res = await fetch(`${API_URL}/api/sportsmain/place-bet`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    return await res.json();
  } catch (err) {
    console.error('Error placing bet:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Fetch match exposures for a user
 * @param {string} matchId - Game/Match ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Exposure data
 */
export const fetchMatchExposures = async (matchId, userId) => {
  try {
    const token = getAuthToken();
    const res = await fetch(`${API_URL}/api/sportsmain/matchexposures/match`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ user_id: userId, match_id: matchId }),
    });
    return await res.json();
  } catch (err) {
    console.error('Error fetching match exposures:', err);
    return { success: false, data: [] };
  }
};

/**
 * Fetch all open bets (matched + unmatched) for a match
 * @param {string} eventId - Event/Match ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Combined open bets data
 */
export const fetchOpenBets = async (eventId, userId) => {
  try {
    const token = getAuthToken();
    const res = await fetch(
      `${API_URL}/api/sportsmain/user-open-bets?user_id=${userId}&eventid=${eventId}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return await res.json();
  } catch (err) {
    console.error('Error fetching open bets:', err);
    return { success: false, data: [] };
  }
};

/**
 * Fetch platform config (buffer times, etc.)
 * @returns {Promise<Object>} Platform config
 */
export const fetchPlatformConfig = async () => {
  try {
    const token = getAuthToken();
    const res = await fetch(`${API_URL}/api/sportsmain/platform-config`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    return data?.data || data || {};
  } catch (err) {
    console.error('Error fetching platform config:', err);
    return {};
  }
};

/**
 * Fetch user's quick stake button values
 * @returns {Promise<Object>} { btn1, btn2, btn3 }
 */
/**
 * Fetch all open sports bets for a user (not event-specific)
 * @param {string} userId - User ID
 * @returns {Promise<Object>} API response with sportsBets, matchedBets, unmatchedBets
 */
export const fetchAllSportsOpenBets = async (userId) => {
  try {
    const token = getAuthToken();
    const res = await fetch(
      `${API_URL}/api/sportsmain/user-sports-open-bets?user_id=${userId}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return await res.json();
  } catch (err) {
    console.error('Error fetching all sports open bets:', err);
    return null;
  }
};

export const fetchStakeButtonValues = async () => {
  try {
    const token = getAuthToken();
    const res = await fetch(`${API_URL}/api/sportsmain/stake-buttons`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const json = await res.json();
    const data = json?.data || json;
    if (data) {
      return {
        btn1: data.btn1 || 2000,
        btn2: data.btn2 || 5000,
        btn3: data.btn3 || 7000,
      };
    }
    return { btn1: 2000, btn2: 5000, btn3: 7000 };
  } catch {
    return { btn1: 2000, btn2: 5000, btn3: 7000 };
  }
};
